import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Loader } from 'common/react/components/Loader';
import {
    playerStatsDataTypeSelector,
    playerStatsNavSelector,
    playerStatsCustomFieldsSelector
} from 'store/modules/mc-player-stats/selectors';
import { EmptyState } from 'common/react/components/EmptyState';
import { Table } from './components/Table';
import {
    composeRowData,
    composeColumnData,
    filterPlayersByTeam,
    resolveRowsFromPlayers,
    resolveRowsFromAverages
} from './utils';
import { useDataType } from 'widgets/statspro/js/components/common/hooks/use-data-type';
import { updateContainerDataType } from './utils/update-container-data-type';
import { setDataType } from 'common/store/modules/mc-player-stats/init';
import {
    useGetMatchPlayerStatsQuery,
    useGetMatchSeasonStatsQuery
} from 'common/store/modules/api/cfs-api';

const POLL_DELAY = PULSE.app.common.CONSTANTS.MATCH_POLL_DELAY;

export const PlayerStatsTable = ({
    matchId,
    homePid,
    awayPid,
    compPid,
    status
}) => {
    const [poll, setPoll] = useState(POLL_DELAY);

    useEffect(() => {
        status !== PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
            ? setPoll(0)
            : setPoll(POLL_DELAY);
    }, [status]);

    const {
        data: matchPlayers,
        isSuccess: matchPlayersIsSuccess,
        isLoading: matchPlayersIsLoading
    } = useGetMatchPlayerStatsQuery(
        { matchId },
        {
            pollingInterval: poll,
            skipPollingIfUnfocused: true
        }
    );

    const {
        data: matchAverages,
        isSuccess: matchAveragesIsSuccess,
        isFetching: matchAveragesIsFetching
    } = useGetMatchSeasonStatsQuery({
        competitionId: compPid,
        teamIds: [homePid, awayPid]
    });

    const matchData = useMemo(() => {
        return {
            ...matchPlayers,
            ...matchAverages
        };
    }, [matchPlayers, matchAverages]);

    const nav = useSelector(playerStatsNavSelector);
    const initialDataType = useSelector(playerStatsDataTypeSelector);
    const { dataType, updateDataType } = useDataType({
        initialDataType,
        onUpdateActionCreator: setDataType
    });
    const [rowData, setRowData] = useState([]);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [noPlayerData, setNoPlayerData] = useState(false);

    const homeAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === homePid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === homePid)?.teamId
    );

    const awayAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === awayPid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === awayPid)?.teamId
    );

    const customFields = useSelector(playerStatsCustomFieldsSelector);

    useEffect(() => {
        // Update the data with nav values when requests have completed
        setNoDataAvailable(false);

        if (!matchPlayersIsSuccess || !matchAveragesIsSuccess || !homeAbbr) {
            // When everything else fails, setNoDataAvailable to true
            // to prevent an infinity loader
            setNoDataAvailable(true);
            return;
        }

        const playersData = filterPlayersByTeam(
            matchData.players ?? [],
            nav.teamPid
        );

        if (playersData.length) {
            resolveRowsFromPlayers({
                playersData,
                matchData,
                dataType: dataType.type,
                noPlayerData,
                updateDataType,
                setRowData,
                setNoDataAvailable
            });
        } else {
            // When there is no player data yet for upcoming matches,
            // fall back to using season averages
            resolveRowsFromAverages({
                matchData,
                teamPid: nav.teamPid,
                dataType: dataType.type,
                noPlayerData,
                updateDataType,
                setNoPlayerData,
                setRowData,
                setNoDataAvailable
            });
        }
    }, [
        matchData,
        dataType.type,
        nav.stats,
        nav.teamPid,
        homeAbbr,
        matchPlayersIsSuccess,
        matchAveragesIsSuccess
    ]);

    useEffect(() => {
        updateContainerDataType(nav.stats);
    }, [nav.stats]);

    /**
     * Get columns.
     */
    const columns = useMemo(
        () => composeColumnData(nav.stats, customFields),
        [nav.stats, rowData, customFields]
    );

    /**
     * Get rows.
     */
    const data = useMemo(
        () =>
            rowData.map((item) =>
                composeRowData(
                    item,
                    columns.filter(
                        (col) =>
                            !['jumperNumber', 'player', 'empty'].includes(
                                col.accessor
                            )
                    ),
                    dataType.type,
                    noPlayerData,
                    homeAbbr,
                    awayAbbr,
                    homePid
                )
            ),
        [columns]
    );

    if (matchPlayersIsLoading || matchAveragesIsFetching) {
        // Loader for waiting for data
        return (
            <div className="mc-player-stats-table__loader">
                <Loader />
            </div>
        );
    } else if (noDataAvailable) {
        // Error handling for when there is no data
        return (
            <div className="mc-player-stats-table__empty-state">
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation="label.stats.error.noStatsAvailable.summary"
                />
            </div>
        );
    }

    return (
        <Table
            key={nav.stats + customFields.length}
            columns={columns}
            data={data}
            modifier="player-stats"
            compPid={compPid}
            matchId={matchId}
        />
    );
};

PlayerStatsTable.propTypes = {
    matchId: PropTypes.string.isRequired,
    homePid: PropTypes.string.isRequired,
    awayPid: PropTypes.string.isRequired,
    compPid: PropTypes.string,
    status: PropTypes.string
};
